/**
 * Customer-side envelope-aware fetch wrapper, mirroring `merchantFetch`
 * for the merchant pages. The full TanStack Query stack in
 * `@orbit/api-client-react` covers our long-lived customer surfaces
 * (cards list, profile, identity QR), but Phase 9 voucher pages only
 * need a handful of one-shot reads + a single create — so a thin
 * fetch helper keeps the page code straight-line without dragging in a
 * new Query mutation per endpoint.
 *
 * Always attaches the customer access token (read from
 * `lib/auth.ts`). On 401 we DO NOT clear tokens here — the access-
 * token rotation lives in the api-client-react interceptor — we just
 * surface the error so callers can show a friendly retry banner.
 */
import { getAccessToken } from "./auth";

export interface ApiOk<T> {
  ok: true;
  data: T;
}
export interface ApiErr {
  ok: false;
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

export async function customerFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<ApiOk<T> | ApiErr> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const token = getAccessToken();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON */
  }

  if (res.ok) {
    const env = (json ?? {}) as { data?: T };
    return { ok: true, data: env.data as T };
  }
  const env = (json ?? {}) as {
    error?: { code?: string; message?: string; details?: unknown };
  };
  return {
    ok: false,
    status: res.status,
    code: env.error?.code ?? "ERROR",
    message: env.error?.message ?? `HTTP ${res.status}`,
    details: env.error?.details,
  };
}
